import { Directive, inject, Input, TemplateRef, ViewContainerRef } from '@angular/core';

/**
 * Structural directive that renders its content if provided value(s) are defined.
 *
 * It accepts a single value or an array. Use `ifPropertyCondition` to choose
 * whether any ("or") or all ("and") of the values must be defined.
 *
 * Inputs:
 * - `ifProperty: any | any[]` — Value(s) to check for `!== undefined`.
 * - `ifPropertyCondition: 'or' | 'and'` — Condition strategy, defaults to `or`.
 *
 * Example:
 * ```html
 * <div *ifProperty="user.organization">Organization exists</div>
 * ```
 */
@Directive({ selector: '[ifProperty]', standalone: true })
export class IfPropertyDirective {
    protected viewContainer = inject(ViewContainerRef);

    protected templateRef = inject(TemplateRef);

    protected hasView = false;

    protected _ifProperty;

    /**
     * Value or list of values to check for defined state.
     */
    @Input() set ifProperty(property: any) {
        this._ifProperty = property;
        this.calculate();
    }

    private _ifPropertyCondition = 'or';

    /**
     * Condition mode: `or` (any defined) or `and` (all defined). Defaults to `or`.
     */
    @Input() set ifPropertyCondition(propertyCondition: any) {
        this._ifPropertyCondition = propertyCondition;
        this.calculate();
    }

    /**
     * Evaluates condition and creates/clears view accordingly.
     */
    calculate() {
        const props = Array.isArray(this._ifProperty) ? this._ifProperty : [this._ifProperty];
        let show;
        if (this._ifPropertyCondition === 'or') {
            // show if at least one value is not undefined
            show = props.reduce((acc, current) => acc || current !== undefined, false);
        } else if (this._ifPropertyCondition === 'and') {
            // show if all values are not undefined
            show = props.reduce((acc, current) => acc && current !== undefined, true);
        }

        if (show && !this.hasView) {
            this.viewContainer.createEmbeddedView(this.templateRef);
            this.hasView = true;
        } else if (!show && this.hasView) {
            this.viewContainer.clear();
            this.hasView = false;
        }
    }
}
